// Website text in English and Korean.

export const copy = {
  en: {
    navHome: "Home",
    navCompany: "Company",
    navPlatform: "Platform",
    navProjects: "Projects",
    navDemo: "Demo",
    navContact: "Contact",
    navPrivacy: "Privacy",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    skipToContent: "Skip to content",
    toTop: "Back to top",
    footerTagline: "AI Digital Interface Platform for Manufacturing",
    footerRights: "© 2026 K Smart Systems Co., Ltd.",

    // Home
    homeOverline: "AI DIGITAL INTERFACE PLATFORM FOR MANUFACTURING",
    homeTitle1: "From Signal",
    homeTitle2: "to <strong>Intelligence.</strong>",
    homeLead: "Connect every production signal.<br>Structure every data point.<br>Turn factory operations into decisions.",
    explorePlatform: "Explore the platform",
    discoverKss: "Discover KSS",

    homeFoot1: "Starting with garment production",
    homeFoot2: "Built for more manufacturing industries",
    threadLabel: "PRODUCTION",
    systemLabel: "DATA",
    intelligenceLabel: "DECISIONS",

    homeSection1: "YOUR FACTORY IN ONE VIEW",
    factorySpeaks: "Know what is happening on your factory floor.",
    homeStatement: "Connect your factory.<br>See what matters.<br>Make better decisions.",
    homeStatementBody: "K SMART SYSTEMS brings information from machines, people and production processes into one place. Your team can follow progress, spot problems and decide what to do next.",
    captureTitle: "Collect production data",
    captureBody: "Collect information from machines, operators and existing systems as work happens.",
    structureTitle: "Bring it all together",
    structureBody: "Organize production data so your team can understand it and compare progress across lines.",
    decideTitle: "Plan your next step",
    decideBody: "See progress, find problems and use the information to decide what to do next.",

    homeSection2: "CONNECTED ARCHITECTURE",
    oneInterface: "<span>One interface.</span><br><span>Every operational layer.</span>",
    architectureIntro: "A modular system designed around the real flow of manufacturing data—<br>from physical events to operational intelligence.",
    physicalLayer: "PHYSICAL LAYER",
    physicalItems: "Machines · Sensors · Operators",
    dataLayer: "DATA LAYER",
    dataItems: "Collection · Validation · Standardization",
    applicationLayer: "APPLICATION LAYER",
    applicationItems: "MES · ERP Integration · Dashboards",
    intelligenceLayer: "INTELLIGENCE LAYER",
    intelligenceItems: "Analytics · Simulation · AI-ready Modules",
    layerInput: "INPUT",
    layerStructure: "STRUCTURE",
    layerConnect: "CONNECT",
    layerDecide: "DECIDE",
    viewArchitecture: "View platform architecture",

    // Factory section
    liveOperations: "PRODUCTION OVERVIEW",
    productionFlow: "Production progress",
    connected: "CONNECTED",
    workStandard: "Work instructions",
    productionAnalysis: "Production reports",
    qualityManagement: "Quality checks",

    homeSection3: "BUILT FOR YOUR FACTORY",
    builtForFloor: "Follow production.<br>Keep your team informed.",
    builtForFloorBody: "KSS brings garment production information into one place. Your team can track work, check quality and compare progress across production lines.",
    seeCapabilities: "Explore factory tools",
    floorStat1: "Record production as work happens",
    floorStat2: "Track each bundle through production",
    floorStat3: "Compare progress across production lines",

    homeSection4: "DEPLOYMENT & COLLABORATION",
    projectTeaserTitle: "<span>Technology</span><br><span>becomes valuable</span><br><span>when it works</span><br><span>in the real world.</span>",
    projectTeaserBody: "Our deployment model connects manufacturers, implementation teams<br>and technology partners around one shared data foundation.",
    viewProjects: "View project framework",

    homeSection5: "PLATFORM DEMO",
    demoTeaserTitle: "See the interface<br>in motion.",
    demoTeaserBody: "Explore role-specific views for operators, factory managers and brands, running on illustrative garment production data.",
    openDemo: "Open the interactive demo",
    ctaOverline: "START A CONVERSATION",
    ctaTitle: "Build the next<br>manufacturing interface.",
    contactUs: "Contact us",// Company
    companyOverline: "COMPANY",
    companyHero: "Connect factory information.<br>Make work easier for your team.",
    companyHeroBody: "K SMART SYSTEMS is a manufacturing technology company connecting equipment, people and production information. We begin with garment production and aim to support more manufacturing industries.",

    ourPurpose: "OUR PURPOSE",
    purposeTitle: "Bring production information<br>together in one place.",
    purposeBody: "Factories have machine records, reports from workers and information in business systems. KSS connects this information so teams can understand production and make informed decisions.",

    howWeBuild: "HOW WE WORK",
    principlesTitle: "Understand the factory.<br>Build the tools it needs.",

    principle1: "Start with the factory",
    principle1Body: "Begin by understanding the people, equipment and daily production process.",

    principle2: "Keep information clear",
    principle2Body: "Check and organize production information consistently to support analysis and future AI use.",

    principle3: "Connect existing systems",
    principle3Body: "Design tools that can connect with the systems a factory already uses.",

    principle4: "Grow step by step",
    principle4Body: "Review how the tools work on the factory floor and add features as they are needed.",

    directionOverline: "OUR DIRECTION",
    directionTitle: "Start with garments.<br>Reach more industries.",

    now: "TODAY",
    nowTitle: "Connect garment production",
    nowBody: "Focus on production records, work instructions, quality checks and system connections.",

    next: "NEXT",
    nextTitle: "Tools for each factory",
    nextBody: "Add analysis tools based on each factory’s needs and prepare for AI use.",

    longTerm: "FUTURE",
    longTitle: "Support more manufacturing industries",
    longBody: "Use our experience in garment production to develop tools for other manufacturing fields.",

    // Platform
    platformOverline: "PLATFORM",
    platformHero: "<span>One continuous data flow.</span><br><span>From the machine</span><br><span>to the decision.</span>",
    platformHeroBody: "A modular platform for collecting, structuring, connecting and applying manufacturing data.",
    platformSystem: "THE KSS SYSTEM",
    currentCapability: "CURRENT CAPABILITY",
    capabilityRoadmap: "DATA-ENABLED AI LAYER",
    product1: "Connected Data Capture",
    product1Body: "Capture production events where they happen through sensors,<br>control units, operator interfaces and system connections.",
    p1a: "IoT sensor interfaces",
    p1b: "Operator interaction",
    p1c: "Completion logic",
    p1d: "Real-time event flow",
    product2: "Manufacturing Data Layer",
    product2Body: "Validate and standardize operational records so every connected process works from a consistent data foundation.",
    p2a: "Data validation",
    p2b: "Work standards",
    p2c: "Process mapping",
    p2d: "ERP integration",
    product3: "Operational Applications",
    product3Body: "Make production status, quality, standards and bottlenecks<br>visible through role-specific interfaces.",
    p3a: "Production analysis",
    p3b: "Quality management",
    p3c: "Line visibility",
    p3d: "Operational dashboards",
    product4: "Decision Intelligence",
    product4Body: "Extend structured operational data into simulation, line balancing, prediction, and factory-specific AI modules.",
    p4a: "Line balancing",
    p4b: "Simulation",
    p4c: "Predictive analysis",
    p4d: "Custom AI modules",
    deploymentModel: "DEPLOYMENT MODEL",
    deploymentTitle: "<span>Start with the operation.</span><br><span>Build the right system.</span>",
    step1: "Discover",
    step1Body: "Map workflows, equipment, data sources, and target decisions.",
    step2: "Connect",
    step2Body: "Configure collection points and connect existing systems.",
    step3: "Validate",
    step3Body: "Verify data logic, interfaces, and operational use cases.",
    step4: "Expand",
    step4Body: "Scale modules across lines, sites, and new decision workflows.",
    platformCtaOverline: "DISCUSS YOUR OPERATION",
    platformCta: "<span>See where your data</span><br><span>can begin.</span>",

    // Projects
    projectsOverline: "PROJECTS & PARTNERSHIPS",
    projectsHero: "<span>Structured for real factories.</span><br><span>Built through collaboration.</span>",
    projectsHeroBody: "KSS brings manufacturers and technology partners together around a shared operational data foundation.",
    engagementAreas: "ENGAGEMENT AREAS",
    engage1: "Connected Production",
    engage1Body: "Connect selected production flows and create real-time operational visibility from source data.",
    e1a: "Line-level data capture",
    e1b: "Production and quality views",
    e1c: "Existing-system connection",
    engage2: "Data Foundation",
    engage2Body: "Define repeatable data models that connect processes, sites, and management views.",
    e2a: "Operational data standards",
    e2b: "Cross-process structure",
    e2c: "Scalable module design",
    engage3: "Intelligence Collaboration",
    engage3Body: "Develop data-enabled analytics and AI modules with domain and technology partners.",
    e3a: "Use-case definition",
    e3b: "Model-ready data pipelines",
    e3c: "Factory-specific modules",
    partnerModel: "PARTNERSHIP MODEL",
    partnerTitle: "<span>One foundation.</span><br><span>Different expertise.</span>",
    partnerBody: "Clear ownership<br>and a shared system architecture<br>let each partner contribute<br>where it creates the most value.",
    manufacturers: "MANUFACTURERS",
    manufacturersBody: "Operational context,<br>production environment<br>and deployment priorities",
    techPartners: "TECHNOLOGY PARTNERS",
    techPartnersBody: "Specialized systems, analytics, AI<br>and integration capabilities",
    sharedFoundation: "SHARED DATA FOUNDATION",
    projectPrinciples: "PROJECT PRINCIPLES",
    pp1: "Start with a defined operational problem",
    pp2: "Protect data, IP, and commercial boundaries",
    pp3: "Validate before expanding scope",
    pp4: "Design every project for repeatability",
    projectCtaOverline: "WORK WITH KSS",
    projectCta: "Bring operations,<br>data, and intelligence together.",

    // Contact
    contactOverline: "CONTACT",
    contactHero: "Let's discuss<br>your factory.",
    contactHeroBody: "Tell us about your production process, existing systems or partnership idea.",
    businessInquiry: "Business inquiries",
    topic1: "Products and deployment",
    topic2: "Technology partnerships",
    topic3: "Company inquiries",
    sendInquiry: "Write an inquiry",
    formName: "Name",
    formSubmit: "Send message",
    formCompany: "Company",
    formEmail: "Email",
    formTopic: "Topic",
    formMessage: "Message",
    formNamePh: "Your name",
    formCompanyPh: "Your company",
    formEmailPh: "name@company.com",
    formMessagePh: "Tell us about your factory, systems or partnership idea.",
    optionProduct: "Products and deployment",
    optionPartner: "Technology partnerships",
    optionCorporate: "Company inquiries",
    formNote: "This opens your email application.<br>Your form entries are not saved on this website.",
    writeEmail: "Write an email",
    formOpened: "An email draft was requested. If it did not open, email us directly.",
    formError: "Complete all fields and check your email address.",
    contactEmailLabel: "Email",
    contactResponseLabel: "Reply",
    contactResponse: "We aim to reply within two business days.",

    // Demo
    demoOverline: "PLATFORM DEMO",
    demoConceptTitle: "Interactive Product Concept",
    demoConceptNote: "Demonstration UI using illustrative data",
    demoHero: "One data foundation.<br>Three points of view.",
    demoHeroBody: "Switch roles to see how the same production signals become a work screen for operators, a control view for managers, and a delivery view for brands.",
    demoNote: "Illustrative sample data. Not connected to a live factory.",

    // Privacy
    privacyOverline: "PRIVACY",
    privacyHero: "Privacy notice.",
    privacyIntro: "This website does not use cookies, analytics or tracking scripts, and does not store any form entries.",
    privacyH1: "Information we process",
    privacyP1: "The contact form composes an email in your own email application. No content is transmitted to or stored on this website's servers. Messages you send arrive at Contact@ksmartsystems.com and are handled solely to respond to your inquiry.",
    privacyH2: "Local preferences",
    privacyP2: "Your language selection (EN / KR) is saved in your browser's local storage under the key kss-language-v2 so it persists between visits. It is never sent to a server.",
    privacyH3: "Third-party services",
    privacyP3: "Web fonts are requested from Google Fonts. Google may record the request according to its own privacy policy.",
    privacyH4: "Contact",
    privacyP4: "For privacy questions, write to Contact@ksmartsystems.com.",
    privacyUpdated: "Last updated: September 2026",

    // Page not found
    notFoundOverline: "404",
    notFoundTitle: "This signal<br>has no destination.",
    notFoundBody: "The page you requested does not exist or has moved.",
    backHome: "Back to home",
  },

  ko: {
    navHome: "홈",
    navCompany: "회사 소개",
    navPlatform: "플랫폼",
    navProjects: "프로젝트",
    navDemo: "데모",
    navContact: "문의",
    navPrivacy: "개인정보",
    openMenu: "메뉴 열기",
    closeMenu: "메뉴 닫기",
    skipToContent: "본문으로 건너뛰기",
    toTop: "맨 위로",
    footerTagline: "제조를 위한 AI 디지털 인터페이스 플랫폼",
    footerRights: "© 2026 K Smart Systems Co., Ltd.",

    // Home
    homeOverline: "제조를 위한 AI 디지털 인터페이스 플랫폼",
    homeTitle1: "신호에서",
    homeTitle2: "<strong>인텔리전스로.</strong>",
    homeLead: "모든 생산 신호를 연결하고<br>모든 데이터를 구조화하여<br>공장의 운영을 의사결정으로 전환합니다.",
    explorePlatform: "플랫폼 살펴보기",
    discoverKss: "KSS 알아보기",
    homeFoot1: "의류·봉제 제조에서 시작",
    homeFoot2: "제조산업 확장을 고려한 설계",
    threadLabel: "실",
    systemLabel: "시스템",
    intelligenceLabel: "인텔리전스",

    homeSection1: "운영 인터페이스",
    factorySpeaks: "공장은 이미 수많은 신호를 보내고 있습니다.",
    homeStatement: "그 신호를<br>보이게 하고,<br>구조화하고,<br>실행 가능하게 만듭니다.",
    homeStatementBody: "K SMART SYSTEMS는 설비, 작업자, 공정, 기업 시스템을<br>하나의 디지털 인터페이스로 연결합니다.<br>생산 현장에서 경영 의사결정까지 이어지는<br>연속적인 데이터 기반을 구축합니다.",
    captureTitle: "발생 지점에서 수집",
    captureBody: "연결된 센서와 작업자 인터페이스,<br>시스템 연동을 통해<br>생산 이벤트를 수집합니다.",
    structureTitle: "하나의 데이터 언어",
    structureBody: "라인과 공정의 신호를 표준화하여<br>운영 데이터를 비교하고<br>이해할 수 있게 만듭니다.",
    decideTitle: "데이터에서 실행으로",
    decideBody: "역할별 인터페이스를 통해<br>실시간 가시성, 분석, 의사결정을 지원합니다.",

    homeSection2: "연결형 아키텍처",
    oneInterface: "하나의 인터페이스.<br>모든 운영 레이어.",
    architectureIntro: "물리적 생산 이벤트부터 운영 인텔리전스까지,<br>실제 제조 데이터의 흐름을 중심으로 설계한<br>모듈형 시스템입니다.",
    physicalLayer: "피지컬 레이어",
    physicalItems: "설비 · 센서 · 작업자",
    dataLayer: "데이터 레이어",
    dataItems: "수집 · 검증 · 표준화",
    applicationLayer: "애플리케이션 레이어",
    applicationItems: "MES · ERP 연동 · 대시보드",
    intelligenceLayer: "인텔리전스 레이어",
    intelligenceItems: "분석 · 시뮬레이션 · AI 활용 모듈",
    layerInput: "입력",
    layerStructure: "구조화",
    layerConnect: "연결",
    layerDecide: "의사결정",
    viewArchitecture: "플랫폼 아키텍처 보기",

    // Factory section
    liveOperations: "생산 현황",
    productionFlow: "생산 진행 상황",
    connected: "연결됨",
    workStandard: "작업 안내",
    productionAnalysis: "생산 보고서",
    qualityManagement: "품질 확인",

    homeSection3: "공장을 위한 시스템",
    builtForFloor: "생산 상황을 확인하고<br>팀과 정보를 공유하세요.",
    builtForFloorBody: "KSS는 의류 생산 정보를 한곳에 모읍니다. 팀은 작업 진행 상황과 품질을 확인하고 생산 라인별 진행 상황을 비교할 수 있습니다.",
    seeCapabilities: "공장 운영 도구 살펴보기",
    floorStat1: "작업 중 발생하는 생산 정보 기록",
    floorStat2: "생산 묶음별 진행 상황 추적",
    floorStat3: "생산 라인별 진행 상황 비교",

    homeSection4: "구축 및 협업",
    projectTeaserTitle: "기술은<br>실제 현장에서 작동할 때<br>가치가 됩니다.",
    projectTeaserBody: "KSS의 구축 모델은 제조기업, 실행 조직, 기술 파트너를<br>하나의 공통 데이터 기반으로 연결합니다.",
    viewProjects: "프로젝트 체계 보기",
    homeSection5: "플랫폼 데모",
    demoTeaserTitle: "움직이는 인터페이스를<br>직접 확인하세요.",
    demoTeaserBody: "작업자, 공장 관리자, 브랜드를 위한 역할별 화면을 예시 생산 데이터로 살펴볼 수 있습니다.",
    openDemo: "인터랙티브 데모 열기",
    ctaOverline: "대화를 시작하세요",
    ctaTitle: "다음 제조 인터페이스를<br>함께 구축합니다.",
    contactUs: "문의하기",

    // Company
    companyOverline: "회사 소개",
    companyHero: "공장의 정보를 연결하고<br>팀의 일을 더 쉽게 만듭니다.",
    companyHeroBody: "K SMART SYSTEMS는 설비, 작업자, 생산 정보를 연결하는 제조 기술 기업입니다. 의류 생산 현장에서 시작해 다양한 제조 현장으로 확장하고자 합니다.",

    ourPurpose: "우리의 목적",
    purposeTitle: "흩어진 생산 정보를<br>한곳에서 확인하세요.",
    purposeBody: "공장에는 설비 기록, 작업자의 보고, 기업 시스템의 정보가 있습니다. KSS는 이 정보를 연결해 팀이 생산 상황을 이해하고 필요한 결정을 내릴 수 있도록 돕습니다.",

    howWeBuild: "일하는 방식",
    principlesTitle: "현장을 이해하고<br>필요한 도구를 만듭니다.",

    principle1: "현장에서 시작합니다",
    principle1Body: "작업자와 설비, 실제 생산 과정을 이해하는 것부터 시작합니다.",

    principle2: "정보를 명확하게 정리합니다",
    principle2Body: "생산 정보를 확인하고 같은 기준으로 정리해 분석과 AI 활용의 기반을 만듭니다.",

    principle3: "기존 시스템과 연결합니다",
    principle3Body: "공장에서 사용하는 시스템과 연결할 수 있도록 설계합니다.",

    principle4: "단계적으로 확장합니다",
    principle4Body: "현장에서 사용 결과를 확인하고 필요한 기능을 차근차근 추가합니다.",

    directionOverline: "앞으로의 방향",
    directionTitle: "의류 생산에서 시작해<br>더 많은 제조 현장으로.",

    now: "현재",
    nowTitle: "의류 생산 정보 연결",
    nowBody: "생산 기록, 작업 안내, 품질 확인과 시스템 연결에 집중합니다.",

    next: "다음 단계",
    nextTitle: "공장에 맞는 분석 도구",
    nextBody: "공장의 필요에 맞춰 분석 기능을 추가하고 AI 활용을 준비합니다.",

    longTerm: "장기 목표",
    longTitle: "다양한 제조 현장으로 확장",
    longBody: "의류 생산에서 쌓은 경험을 바탕으로 다른 제조 분야에도 적용할 수 있는 도구를 개발하고자 합니다.",
    // Platform
    platformOverline: "플랫폼",
    platformHero: "<span>하나로 이어지는 데이터 흐름.</span><br><span>설비에서 의사결정까지.</span>",
    platformHeroBody: "제조 데이터를 수집하고 구조화하며 연결하고 활용하기 위한 모듈형 플랫폼입니다.",
    platformSystem: "KSS 시스템",
    currentCapability: "현재 제공 범위",
    capabilityRoadmap: "데이터 기반 AI 레이어",
    product1: "연결형 데이터 수집",
    product1Body: "센서, 제어장치, 작업자 인터페이스, 시스템 연동을 통해<br>생산 이벤트가 발생하는 지점에서 데이터를 수집합니다.",
    p1a: "IoT 센서 인터페이스",
    p1b: "작업자 인터랙션",
    p1c: "완료 처리 로직",
    p1d: "실시간 이벤트 흐름",
    product2: "제조 데이터 레이어",
    product2Body: "운영 기록을 검증하고 표준화하여<br>연결된 모든 공정이 일관된 데이터 기반 위에서 작동하게 합니다.",
    p2a: "데이터 검증",
    p2b: "작업표준",
    p2c: "공정 매핑",
    p2d: "ERP 연동",
    product3: "운영 애플리케이션",
    product3Body: "역할별 인터페이스를 통해 생산현황, 품질, 작업표준, 병목을 명확하게 보여줍니다.",
    p3a: "생산분석",
    p3b: "품질관리",
    p3c: "라인 가시성",
    p3d: "운영 대시보드",
    product4: "의사결정 인텔리전스",
    product4Body: "구조화된 운영 데이터를<br>시뮬레이션, 라인 밸런싱, 예측, 공장별 맞춤 AI 모듈로 확장합니다.",
    p4a: "라인 밸런싱",
    p4b: "시뮬레이션",
    p4c: "예측 분석",
    p4d: "맞춤형 AI 모듈",
    deploymentModel: "구축 모델",
    deploymentTitle: "<span>운영에서 시작해,</span><br><span>필요한 시스템을 만듭니다.</span>",
    step1: "진단",
    step1Body: "업무 흐름, 설비, 데이터 원천,<br>목표 의사결정을 정의합니다.",
    step2: "연결",
    step2Body: "데이터 수집 지점을 구성하고<br>기존 시스템을 연결합니다.",
    step3: "검증",
    step3Body: "데이터 로직, 인터페이스,<br>운영 활용 사례를 검증합니다.",
    step4: "확장",
    step4Body: "라인과 사업장, 새로운 의사결정 업무로<br>모듈을 확장합니다.",
    platformCtaOverline: "운영환경을 논의하세요",
    platformCta: "데이터가 어디에서<br>시작될 수 있는지 확인하세요.",

    // Projects
    projectsOverline: "프로젝트 및 파트너십",
    projectsHero: "<span>실제 공장을 위해 구조화하고,</span><br><span>협업을 통해 완성합니다.</span>",
    projectsHeroBody: "KSS는 제조기업과 기술 파트너를 하나의 공통 운영 데이터 기반으로 연결합니다.",
    engagementAreas: "협업 영역",
    engage1: "연결형 생산운영",
    engage1Body: "선택된 생산 흐름을 연결하고 원천 데이터를 기반으로 실시간 운영 가시성을 제공합니다.",
    e1a: "라인 단위 데이터 수집",
    e1b: "생산 및 품질 가시화",
    e1c: "기존 시스템 연결",
    engage2: "데이터 기반 구축",
    engage2Body: "공정, 사업장, 경영 관점을 연결하는<br>반복 가능한 데이터 모델을 정의합니다.",
    e2a: "운영 데이터 표준",
    e2b: "공정 간 데이터 구조",
    e2c: "확장형 모듈 설계",
    engage3: "인텔리전스 협업",
    engage3Body: "도메인 및 기술 파트너와 함께<br>데이터 기반 분석 및 AI 모듈을 개발합니다.",
    e3a: "활용 사례 정의",
    e3b: "모델 학습용 데이터 파이프라인",
    e3c: "공장별 맞춤형 모듈",
    partnerModel: "파트너십 모델",
    partnerTitle: "하나의 기반.<br>서로 다른 전문성.",
    partnerBody: "명확한 권리관계와 공통 시스템 아키텍처를 통해<br>각 파트너가 가장 가치 있는 영역에 기여합니다.",
    manufacturers: "제조기업",
    manufacturersBody: "운영 맥락, 생산환경, 구축 우선순위",
    techPartners: "기술 파트너",
    techPartnersBody: "전문 시스템, 분석, AI, 통합 역량",
    sharedFoundation: "공통 데이터 기반",
    projectPrinciples: "프로젝트 원칙",
    pp1: "명확하게 정의된 운영 문제에서 시작합니다",
    pp2: "데이터, 지식재산권, 상업적 경계를 보호합니다",
    pp3: "범위를 확장하기 전에 먼저 검증합니다",
    pp4: "모든 프로젝트를 반복 가능하도록 설계합니다",
    projectCtaOverline: "KSS와 협업하세요",
    projectCta: "운영과 데이터,<br>인텔리전스를 연결합니다.",

    // Contact
    contactOverline: "문의",
    contactHero: "다음 신호를<br>함께 연결합니다.",
    contactHeroBody: "운영환경, 시스템, 파트너십 아이디어를 알려주세요.<br>가장 중요한 질문에서 시작하겠습니다.",
    businessInquiry: "비즈니스 문의",
    topic1: "제품 및 구축",
    topic2: "기술 파트너십",
    topic3: "기업 문의",
    sendInquiry: "문의 작성",
    formName: "이름",
    formSubmit: "메시지 보내기",
    formCompany: "회사명",
    formEmail: "이메일",
    formTopic: "문의 유형",
    formMessage: "문의 내용",
    formNamePh: "성함",
    formCompanyPh: "회사명",
    formEmailPh: "name@company.com",
    formMessagePh: "운영환경, 시스템, 파트너십 아이디어를 알려주세요.",
    optionProduct: "제품 및 구축",
    optionPartner: "기술 파트너십",
    optionCorporate: "기업 문의",
    formNote: "전송 시 이메일 프로그램이 열립니다.<br>이 웹사이트에는 입력 정보가 저장되지 않습니다.",
    writeEmail: "이메일 작성",
    formOpened: "이메일 프로그램이 열렸습니다.",
    formError: "모든 항목을 입력하고 올바른 이메일 주소를 확인해 주세요.",
    contactEmailLabel: "이메일",
    contactResponseLabel: "회신",
    contactResponse: "영업일 기준 2일 이내에 회신합니다.",

    // Demo
    demoOverline: "플랫폼 데모",
    demoConceptTitle: "인터랙티브 제품 컨셉",
    demoConceptNote: "예시 데이터를 활용한 데모 UI",
    demoHero: "하나의 데이터 기반.<br>세 가지 관점.",
    demoHeroBody: "역할을 전환하며 동일한 생산 신호가 작업자의 작업 화면, 관리자의 관제 화면, 브랜드의 납기 화면으로 어떻게 바뀌는지 확인하세요.",
    demoNote: "예시 데이터입니다. 실제 공장과 연결되어 있지 않습니다.",

    // Privacy
    privacyOverline: "개인정보 처리방침",
    privacyHero: "개인정보 처리방침.",
    privacyIntro: "이 웹사이트는 쿠키, 분석 도구, 추적 스크립트를 사용하지 않으며 입력한 정보를 저장하지 않습니다.",
    privacyH1: "처리하는 정보",
    privacyP1: "문의 양식은 방문자의 이메일 프로그램에서 이메일을 작성합니다. 어떠한 내용도 이 웹사이트 서버로 전송되거나 저장되지 않습니다. 보내신 메일은 Contact@ksmartsystems.com으로 도착하며 문의 응답 목적으로만 사용됩니다.",
    privacyH2: "로컬 설정",
    privacyP2: "언어 선택(EN / KR)은 브라우저의 로컬 스토리지에 kss-language-v2 키로 저장되어 다음 방문 시에도 유지됩니다. 서버로 전송되지 않습니다.",
    privacyH3: "제3자 서비스",
    privacyP3: "웹 폰트는 Google Fonts에서 불러옵니다. Google은 자체 개인정보 처리방침에 따라 요청을 기록할 수 있습니다.",
    privacyH4: "문의",
    privacyP4: "개인정보 관련 문의는 Contact@ksmartsystems.com으로 보내주세요.",
    privacyUpdated: "최종 수정: 2026년 9월",

    // Page not found
    notFoundOverline: "404",
    notFoundTitle: "이 신호에는<br>도착지가 없습니다.",
    notFoundBody: "요청하신 페이지가 존재하지 않거나 이동되었습니다.",
    backHome: "홈으로 돌아가기",
  },
};

export const pageTitles = {
  home: {
    en: "K SMART SYSTEMS — Sewing machine signals to production decisions",
    ko: "K SMART SYSTEMS — 재봉기 신호에서 생산 의사결정으로",
  },
  company: {
    en: "Company — K SMART SYSTEMS",
    ko: "회사 소개 — K SMART SYSTEMS",
  },
  platform: {
    en: "Platform — K SMART SYSTEMS",
    ko: "플랫폼 — K SMART SYSTEMS",
  },
  projects: {
    en: "Projects — K SMART SYSTEMS",
    ko: "프로젝트 — K SMART SYSTEMS",
  },
  demo: {
    en: "Demo: digital twin and role views — K SMART SYSTEMS",
    ko: "데모: 디지털 트윈과 역할별 화면 — K SMART SYSTEMS",
  },
  contact: {
    en: "Contact — K SMART SYSTEMS",
    ko: "문의 — K SMART SYSTEMS",
  },
  privacy: {
    en: "Privacy — K SMART SYSTEMS",
    ko: "개인정보 처리방침 — K SMART SYSTEMS",
  },
  notFound: {
    en: "Page not found — K SMART SYSTEMS",
    ko: "페이지를 찾을 수 없음 — K SMART SYSTEMS",
  },
};
Object.assign(copy.en, {
  demoRoleOperator: "Operator",
  demoRoleOperatorSub: "Sewing station terminal",
  demoRoleManager: "Factory Manager",
  demoRoleManagerSub: "Production line overview",
  demoRoleBrand: "Brand / Client",
  demoRoleBrandSub: "Order progress and delivery",

  demoStation: "STATION",
  demoOperation: "OPERATION",
  demoStyle: "STYLE",
  demoBundle: "CURRENT BUNDLE",
  demoTargetHour: "TARGET / HOUR",
  demoCompletedToday: "COMPLETED TODAY",
  demoEfficiency: "EFFICIENCY",
  demoSam: "SAM",
  demoMinutes: "min",
  demoPieces: "pcs",
  demoCompleteBundle: "Complete bundle",
  demoBundleDone: "Bundle recorded. Next bundle loaded.",

  demoReportDefect: "REPORT DEFECT",
  demoDefectSkip: "Skipped stitch",
  demoDefectBroken: "Broken stitch",
  demoDefectPucker: "Puckering",
  demoDefectMeasure: "Measurement",
  demoDefectLogged: "Defect recorded in the quality log.",
  demoWorkStandard: "WORK STANDARD",
  demoQuality: "QUALITY",

  demoHourly: "HOURLY OUTPUT",
  demoLines: "LINES",
  demoOutputVsTarget: "OUTPUT VS TARGET",
  demoWip: "WIP",
  demoDhu: "DHU",
  demoDhuFull: "Defects per hundred units",
  demoOperators: "OPERATORS",
  demoBottleneck: "BOTTLENECK",
  demoAlerts: "PRODUCTION ALERTS",
  demoLineDetail: "LINE DETAIL",
  demoStations: "Stations",
  demoAllLines: "All lines",

  demoOrders: "ORDERS IN PRODUCTION",
  demoOrder: "ORDER",
  demoQty: "QUANTITY",
  demoProgress: "PROGRESS",
  demoShipDate: "PLANNED SHIPMENT",
  demoRisk: "DELIVERY RISK",
  demoRiskLow: "On track",
  demoRiskMid: "Watch",
  demoRiskHigh: "At risk",
  demoStageCut: "Cutting",
  demoStageSew: "Sewing",
  demoStageFinish: "Finishing",
  demoStagePack: "Packing",
  demoAqlPass: "AQL pass rate",
  demoInline: "Inline inspection",
  demoFinal: "Final inspection",
  demoLastUpdate: "Last update",
  demoUnits: "units",
  demoPerHour: "/hr",
  demoTarget: "target",
  demoActual: "actual",

  demoNote: "Illustrative sample data. Not connected to a live factory.",
});

Object.assign(copy.ko, {
  demoRoleOperator: "작업자",
  demoRoleOperatorSub: "봉제 작업 단말",
  demoRoleManager: "공장 관리자",
  demoRoleManagerSub: "생산 라인 현황",
  demoRoleBrand: "브랜드 / 고객",
  demoRoleBrandSub: "주문 진행 및 납품 현황",

  demoStation: "작업 위치",
  demoOperation: "공정",
  demoStyle: "스타일",
  demoBundle: "현재 번들",
  demoTargetHour: "시간당 목표",
  demoCompletedToday: "금일 완료",
  demoEfficiency: "효율",
  demoSam: "표준시간",
  demoMinutes: "분",
  demoPieces: "장",
  demoCompleteBundle: "번들 완료",
  demoBundleDone: "번들이 기록되었습니다. 다음 번들을 불러왔습니다.",

  demoReportDefect: "불량 보고",
  demoDefectSkip: "땀 건너뜀",
  demoDefectBroken: "실 끊김",
  demoDefectPucker: "봉제 주름",
  demoDefectMeasure: "치수 불량",
  demoDefectLogged: "불량이 품질 기록에 추가되었습니다.",
  demoWorkStandard: "작업표준",
  demoQuality: "품질",

  demoHourly: "시간당 생산량",
  demoLines: "라인",
  demoOutputVsTarget: "생산량 및 목표",
  demoWip: "재공",
  demoDhu: "DHU",
  demoDhuFull: "100장당 불량 수",
  demoOperators: "작업자",
  demoBottleneck: "병목",
  demoAlerts: "생산 알림",
  demoLineDetail: "라인 상세",
  demoStations: "작업 위치",
  demoAllLines: "전체 라인",

  demoOrders: "생산 중인 주문",
  demoOrder: "주문",
  demoQty: "수량",
  demoProgress: "진행률",
  demoShipDate: "출고 예정",
  demoRisk: "납기 위험",
  demoRiskLow: "정상",
  demoRiskMid: "주의",
  demoRiskHigh: "위험",
  demoStageCut: "재단",
  demoStageSew: "봉제",
  demoStageFinish: "완성",
  demoStagePack: "포장",
  demoAqlPass: "AQL 합격률",
  demoInline: "공정 검사",
  demoFinal: "최종 검사",
  demoLastUpdate: "최근 업데이트",
  demoUnits: "장",
  demoPerHour: "/시간",
  demoTarget: "목표",
  demoActual: "실적",

  demoNote: "예시 데이터입니다. 실제 공장과 연결되어 있지 않습니다.",
});
